import { createContext, ReactNode, useContext, useMemo } from 'react';
import { UseQueryState } from 'urql';
import useOfflineQuery from 'hooks/useOfflineQuery';
import { InventoryDashboardNewQuery, useInventoryDashboardNewQuery } from 'generated/graphql';
import { useIsReportsPdfMode } from '../useIsReportsPdfMode';
import { useReportsDashboard } from '../useReportsDashboard';

interface InventoryDashboardContextProps {
  inventoryDashboardNewResult: UseQueryState<InventoryDashboardNewQuery>;
  inventoryDashboardFetching: boolean;
  refreshInventoryDashboardNewResult: () => void;
}

const Context = createContext<InventoryDashboardContextProps | undefined>(undefined);

export const InventoryDashboardProvider = ({ children }: { children: ReactNode }) => {
  const { reportsDashboardNewQueryInput } = useReportsDashboard();
  const isReportsPdfMode = useIsReportsPdfMode();

  const [inventoryDashboardNewResult, refreshInventoryDashboardNewResult] = useOfflineQuery(
    'inventory-dashboard-new-query',
    useInventoryDashboardNewQuery,
    {
      variables: { input: reportsDashboardNewQueryInput },
      requestPolicy: 'network-only',
      pause: isReportsPdfMode,
    },
  );

  const inventoryDashboardFetching =
    (inventoryDashboardNewResult.fetching || inventoryDashboardNewResult.stale) ?? false;

  const value = useMemo<InventoryDashboardContextProps>(
    () => ({
      inventoryDashboardNewResult,
      inventoryDashboardFetching,
      refreshInventoryDashboardNewResult,
    }),
    [inventoryDashboardNewResult, inventoryDashboardFetching, refreshInventoryDashboardNewResult],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInventoryDashboard = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useInventoryDashboard must be used within an InventoryDashboardProvider');
  }
  return result;
};
